// @ts-nocheck

import React, { useState, useEffect, useMemo, useRef } from 'react';
import { TransitionGroup, CSSTransition } from 'react-transition-group';
import styled from 'styled-components';

import { NotConnected1 } from '../../assets/img';
import { StorageKeys } from '../../constants/storage';
import { HistoryItemContext } from '../../contexts/HistoryItemContext';
import { useLocalStorage, useShowLogin } from '../../hooks';
import useRouter from '../../hooks/useRouter';
import { __ } from '../../i18n/l10n';
import theme from '../../styled-components/dark';
import { AutoScrollAnchor } from '../../styled-components/shared';
import type { PreviousPosition } from '../../types/pages';
import { instantScrollToAnchor } from '../../utils/smoothScroll';
import HistoryFiltersBarContainer from '../HistoryFiltersBar/HistoryFiltersBarContainer';
import HistoryItemContainer from '../HistoryItem/HistoryItemContainer';
import HistoryItemDetailContainer from '../HistoryItemDetail/HistoryItemDetailContainer';
import HistoryPaginationContainer from '../HistoryPagination/HistoryPaginationContainer';
import NotFoundView from '../NotFoundView';
import WinaLoader from '../WinaLoader';

const ANIMATION_DURATION = 300;

const Wrapper = styled.div`
  position: relative;
`;

const Content = styled.div`
  color: ${({ theme }) => theme.common.colors.defaultFontColor};
`;

const StyledHistoryItemContainer = styled(HistoryItemContainer)`
  transition: all ${ANIMATION_DURATION}ms ease-in-out;

  &.history-item-exit {
    opacity: 0;
    transform: scale(0.9);
  }
`;

const HistoryContent = styled.div`
  display: flex;
  flex-direction: column;
`;

const HistoryList = styled.div``;

const EmptyList = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 200px;
  color: ${theme.History.labelColor};
`;

const StyledHistoryFiltersBarContainer = styled(HistoryFiltersBarContainer)`
  padding: 0.74em 0.67em 0.67em;
`;

const StyledHistoryPaginationContainer = styled(HistoryPaginationContainer)`
  margin: 0.5em;
  padding-top: 0;
`;

export const HISTORY_ROUTE_ANCHOR_ID = 'history-route-anchor';

type PropsType = {
  betslipIds?: Array<number> | null;
  connected: boolean;
  loading: boolean;
  currentPage: number;
  setFilter: (filter: unknown, page?: number) => void;
  filter?: number;
  previousPosition?: PreviousPosition;
  resetPosition?: () => void;
};

const History = ({
  betslipIds,
  loading,
  connected,
  currentPage,
  setFilter,
  filter,
  previousPosition,
  resetPosition,
}: PropsType) => {
  const {
    location,
    params: { status },
    history,
  } = useRouter();
  const [historyBetSlipIds, setHistoryBetSlipIds] = useState([]);
  const [hasFilterChanged, setHasFilterChanged] = useState(false);

  const [betslipReference, setBetslipReference] = useState(null);
  const [isCompact, setIsCompact] = useLocalStorage(StorageKeys.USER_PREFS_COMPACT_HISTORY, true);

  const { pageNumber: previousPage, anchorId } = previousPosition ?? {};

  const isReadyToScrollOnPreviousPosition = useMemo(() => !loading && !!betslipIds, [loading, betslipIds]);
  const fromBackButton = useMemo(() => history?.getLastActionType?.() === 'POP', [history]);

  const previousStatus = useRef(status);
  const resettingPosition = useRef(false);

  useEffect(() => {
    if (betslipIds) {
      setHistoryBetSlipIds(betslipIds);
      // reset position if filter has changed and received new betslipIds
      setHasFilterChanged(false);
    }
  }, [betslipIds]);

  useEffect(() => {
    setHasFilterChanged(true);
  }, [filter]);

  useEffect(() => {
    if (resettingPosition.current) {
      resettingPosition.current = false;
    } else {
      const page = fromBackButton ? previousPage : undefined;
      setFilter(status, page);
    }
  }, [fromBackButton, setFilter, status, previousPage]);

  useEffect(() => {
    if (fromBackButton && isReadyToScrollOnPreviousPosition && anchorId) {
      instantScrollToAnchor(anchorId);
    }
  }, [fromBackButton, isReadyToScrollOnPreviousPosition, anchorId, resetPosition]);

  useEffect(() => {
    if (previousPosition && (previousStatus.current !== status || previousPage !== currentPage)) {
      resetPosition?.();
      resettingPosition.current = true;
      previousStatus.current = status;
    }
  }, [previousPosition, status, resetPosition, previousPage, currentPage]);

  const HistoryBetsList = useMemo(() => {
    if (loading || !betslipIds) {
      return (
        <EmptyList>
          <WinaLoader delayed />
        </EmptyList>
      );
    }

    if (betslipIds.length === 0) {
      return <EmptyList>{__('No bets')}</EmptyList>;
    }

    return (
      <HistoryContent data-testid="history-content">
        {currentPage !== 0 ? <StyledHistoryPaginationContainer /> : null}
        <HistoryList>
          {/* Disable the transition the history filter change */}
          <TransitionGroup exit={!hasFilterChanged}>
            {historyBetSlipIds.map((id, item) => (
              <CSSTransition key={`betslip-${id}`} timeout={ANIMATION_DURATION} classNames="history-item" unmountOnExit>
                <HistoryItemContext.Provider value={{ isFirstItem: item === 0 }} key={`betslip-${id}`}>
                  <StyledHistoryItemContainer
                    id={id}
                    isCompact={isCompact}
                    currentPage={currentPage}
                    anchorToOpen={fromBackButton ? anchorId : undefined}
                  />
                </HistoryItemContext.Provider>
              </CSSTransition>
            ))}
          </TransitionGroup>
        </HistoryList>
        <StyledHistoryPaginationContainer />
      </HistoryContent>
    );
  }, [loading, betslipIds, hasFilterChanged, currentPage, historyBetSlipIds, isCompact, fromBackButton, anchorId]);

  useEffect(() => {
    const currBetslipRef = location?.state?.betslipReference;
    if (currBetslipRef) {
      setBetslipReference(currBetslipRef);
    }
  }, [location]);

  useShowLogin();

  if (connected && betslipReference) {
    return <HistoryItemDetailContainer betslipReference={betslipReference} />;
  }

  return (
    <Wrapper data-testid="history">
      <AutoScrollAnchor data-anchorid={HISTORY_ROUTE_ANCHOR_ID} />
      <Content>
        {connected ? (
          <>
            <StyledHistoryFiltersBarContainer isCompact={isCompact} setIsCompact={setIsCompact} />
            {HistoryBetsList}
          </>
        ) : (
          <NotFoundView label={__('You must be logged in to see this page')} imgPath={NotConnected1} />
        )}
      </Content>
    </Wrapper>
  );
};

export default History;
